// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import TitleTypography from "@awc/App/Components/Common/TitleTypography";
import XngZone from "@awc/App/Components/Common/XngZone";
import { CustomAppState } from "@awc/App/Store/Reducers";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AccountMessages from "@insite/content-library/Widgets/MyAccount/AccountMessages";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import * as React from "react";
import { connect } from "react-redux";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => {
    return {
        addressPageLink: getPageLinkByPageType(state, "AddressesPage")?.url,
        myListsPageLink: getPageLinkByPageType(state, "MyListsPage")?.url,
        isMobileScreen: (state as CustomAppState).xCommon?.isMobileScreen,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasShellContext;

export interface MyAccountViewStyles {
    quickLinksGridItem?: GridItemProps;
    mainSectionGridItem?: GridItemProps;
    mainSectionGridContainer?: GridContainerProps;
    accountMessagesGridItem?: GridItemProps;
    dashboardWidgetsGridItem?: GridItemProps;
    // XNG-Change
    mainContainer?: GridContainerProps;
    pageTitleGridItem?: GridItemProps;
    welcomeWidgetsGridItem?: GridItemProps;
    extraCardsGridItem?: GridItemProps;
    salesRepGridItem?: GridItemProps;
}

export const myAccountViewStyles: MyAccountViewStyles = {
    quickLinksGridItem: { width: [12, 12, 12, 2, 2] },
    mainSectionGridItem: { width: [12, 12, 12, 10, 10] },
    accountMessagesGridItem: { width: 12 },
    dashboardWidgetsGridItem: { width: 12 },
};

const styles = myAccountViewStyles;

class MyAccountView extends React.PureComponent<Props> {
    render() {
        // XNG-Change: Structure Update
        return (
            <GridContainer {...styles.mainContainer}>
                <GridItem {...styles.quickLinksGridItem}>
                    <XngZone contentId={this.props.id} zoneName="NavigationList" hint="Links & Profile" />
                </GridItem>
                <GridItem {...styles.mainSectionGridItem}>
                    <XngZone contentId={this.props.id} zoneName="PageTitle" hint="Page Title" />
                    <GridContainer {...styles.mainSectionGridContainer}>
                        <GridItem {...styles.welcomeWidgetsGridItem}>
                            <XngZone contentId={this.props.id} zoneName="WelcomeWidgets" hint="Welcome Widgets" />
                        </GridItem>
                        <GridItem {...styles.accountMessagesGridItem}>
                            <AccountMessages />
                        </GridItem>
                        <GridItem {...styles.dashboardWidgetsGridItem}>
                            <XngZone contentId={this.props.id} zoneName="DashboardWidgets" hint="Dashboard Widgets" />
                        </GridItem>
                        <GridItem
                            {...styles.salesRepGridItem}
                            {...{
                                showGrid:
                                    this.props.isMobileScreen ||
                                    (this.props.shellContext.isInShell && this.props.shellContext.isEditing),
                            }}
                        >
                            <XngZone
                                contentId={this.props.id}
                                zoneName="SalesRep"
                                hint="Sales Rep Information Widgets"
                            />
                            <TitleTypography
                                linkText={translate("View All")}
                                link={this.props.addressPageLink}
                                displayAsButton={true}
                            >
                                {translate("Addresses")}
                            </TitleTypography>
                            <TitleTypography
                                linkText={translate("View All")}
                                link={this.props.myListsPageLink}
                                displayAsButton={true}
                            >
                                {translate("My Lists")}
                            </TitleTypography>
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
        );
    }
}

const myAccountViewWidget: WidgetModule = {
    component: connect(mapStateToProps)(withIsInShell(MyAccountView)),
    definition: {
        allowedContexts: ["MyAccountPage"],
        group: "My Account",
    },
};

export default myAccountViewWidget;
